import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import JV from '@pgrabovets/json-view'; // eslint-disable-line import/no-extraneous-dependencies
import Dexie from 'dexie'; // eslint-disable-line import/no-extraneous-dependencies,import/no-named-as-default
import { useLiveQuery } from 'dexie-react-hooks';

import Button from 'components/form/Button.component';
import useTip from 'hooks/useTip.hook';
import { selectCurrentSalon } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import { fromAnyToPrettyJson } from 'utils/converters/json.util';
import { locationReload } from 'utils/dom/location.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';

import CN from './DatabasePage.module.scss';

const DB_NAME = `SlickPaymentAudit`;
const DB_SCHEMA_CHECKOUT = 'Checkout';
const DB_VERSION = 1;

const COLLECTION = (() => {
  const db = new Dexie(DB_NAME);

  db.version(DB_VERSION).stores({
    [DB_SCHEMA_CHECKOUT]: '&id,createdAt,modifiedAt',
  });

  return db[DB_SCHEMA_CHECKOUT];
})();

const Rows = ({ value }) => {
  const dataCellRef = useRef(null);

  useEffect(() => {
    const tree = JV.renderJSON(value, dataCellRef.current);
    return () => void JV.destroy(tree);
  }, [value]);

  return (
    <tbody>
      <tr>
        <td className={CN.idCell}>
          {value?.id} @ {value?.createdAt}
        </td>
      </tr>
      <tr>
        <td ref={dataCellRef} className={CN.dataCell}></td>
      </tr>
    </tbody>
  );
};

const reverse = (array) => (isNotFullArray(array) ? array : [...array].reverse());

const DatabasePage = () => {
  const { val: isAscending, inv: isDescending, yay: sortAscending, nay: sortDescending } = useTip(false);
  const handleReload = useCallback(() => void locationReload(), []);

  const schemaEntries = useLiveQuery(() => Object.entries(COLLECTION.schema ?? {}));
  const count = useLiveQuery(() => COLLECTION.count());
  const items = (isAscending ? alwaysSame : reverse)(useLiveQuery(() => COLLECTION.toArray()));

  return (
    <div data-bem="DatabasePage" style={{ background: 'gray', width: '100vw', height: '100vh' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div>
          <Button tag="DatabasePage__add-button" onClick={handleReload}>
            Reload Page
          </Button>
          <Button tag="DatabasePage__asc-button" onClick={sortAscending} disabled={isAscending}>
            Sort Ascending
          </Button>
          <Button tag="DatabasePage__desc-button" onClick={sortDescending} disabled={isDescending}>
            Sort Descending
          </Button>
        </div>

        <table style={{ background: '#ccc', fontFamily: 'monospace' }}>
          <thead>
            <tr>
              <th colSpan={2} style={{ fontSize: '2em' }}>
                {count} items will appear down here
              </th>
            </tr>
          </thead>
          {asArray(items).map((item) => (
            <Rows key={item?.id} value={item} />
          ))}
        </table>

        <div style={{ color: '#999', background: '#333' }}>
          <table style={{ fontFamily: 'monospace' }}>
            <thead>
              <tr>
                <th colSpan={2} style={{ fontSize: '2em' }}>
                  Schema
                </th>
              </tr>
            </thead>
            <tbody>
              {asArray(schemaEntries).map(([key, val]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>
                    <pre>{fromAnyToPrettyJson(val)}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const DatabasePageHoc = connect((state) => ({
  currentSalon: selectCurrentSalon(state),
}))(DatabasePage);

export default DatabasePageHoc;
